// Date helpers shared across the health comparator (form mapper, results page,
// callback modal). Centralizes the DD/MM/YYYY ⇄ ISO/YMD conversions that were
// previously re-implemented in each file.

export const FR_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/
export const ZIP_REGEX = /^\d{5}$/

// ISO date (YYYY-MM-DD...) → DD/MM/YYYY
export const isoToFrDate = (isoDate: string | null | undefined): string => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
}

// DD/MM/YYYY → ISO date (YYYY-MM-DDT00:00:00+01:00)
export const frDateToApiDate = (formDate: string): string => {
    if (!formDate) return ''
    const [day, month, year] = formDate.split('/')
    if (!day || !month || !year) return ''
    return `${year}-${month}-${day}T00:00:00+01:00`
}

// YYYY-MM-DD → DD/MM/YYYY (leaves the input untouched if it doesn't match)
export const ymdToFrDate = (ymd: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : ymd
}

// Date → YYYY-MM-DD (local time)
export const dateToYmd = (d: Date): string => {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
}

// Date → DD/MM/YYYY (local time)
export const dateToFrDate = (d: Date): string => {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${dd}/${mm}/${d.getFullYear()}`
}

// Latest DD/MM/YYYY birth date for someone who must be at least `minAge` years
// old today (minAge=18 → today minus 18 years). Use minAge=0 to cap at today.
export const maxBirthDateForAge = (minAge: number): string => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setFullYear(d.getFullYear() - minAge)
    return dateToFrDate(d)
}

// Earliest DD/MM/YYYY birth date for someone who must be at most `maxAge` years
// old today. Inclusive of the whole max-age year (mirrors the schema's `age > maxAge`).
export const minBirthDateForAge = (maxAge: number): string => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setFullYear(d.getFullYear() - maxAge - 1)
    d.setDate(d.getDate() + 1)
    return dateToFrDate(d)
}

const frToYmd = (frDate: string): string => {
    const [day, month, year] = frDate.split('/')
    return `${year}-${month}-${day}`
}

// True when a DD/MM/YYYY string is a real calendar day within the age bounds the birth-date pickers enforce.
export const isBirthdateWithinAges = (s: string, minAge: number, maxAge: number): boolean => {
    if (!FR_DATE_REGEX.test(s)) return false
    const [day, month, year] = s.split('/').map(Number)
    const d = new Date(year, month - 1, day)
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return false
    const ymd = dateToYmd(d)
    return ymd >= frToYmd(minBirthDateForAge(maxAge)) && ymd <= frToYmd(maxBirthDateForAge(minAge))
}

// « Dès que possible » covers from the day after the subscription.
export const asapEffectiveDate = (): string => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    date.setDate(date.getDate() + 1)
    return dateToFrDate(date)
}

export const nextMonthEffectiveDate = (): string => {
    const today = new Date()
    return dateToFrDate(new Date(today.getFullYear(), today.getMonth() + 1, 1))
}

// A stored date before tomorrow (new quotes are created with today's date) reads as « Dès que possible ».
export const getValidEffectiveDate = (isoDate: string | null | undefined): string => {
    if (!isoDate) return asapEffectiveDate()

    const date = new Date(isoDate)
    const tomorrow = new Date()
    tomorrow.setHours(0, 0, 0, 0)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (isNaN(date.getTime()) || date < tomorrow) {
        return asapEffectiveDate()
    }

    return isoToFrDate(isoDate)
}

// True when a DD/MM/YYYY string is well-formed and resolves to today or a
// future date.
export const isEffectiveDateValid = (s: string): boolean => {
    if (!FR_DATE_REGEX.test(s)) return false
    const [day, month, year] = s.split('/').map(Number)
    const d = new Date(year, month - 1, day)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return d >= today
}
